import type { GameItem } from '../../i18n'
import { shuffle } from '../utils'

export type PuzzleDifficulty = 'easy' | 'medium' | 'hard'
export type PuzzleCutType = 'vertical' | 'horizontal' | 'diagonal'
export type PuzzleMode = 'puzzle' | 'pairs'

export interface PieceDef {
  id: string
  col: number
  row: number
  gridCols: number
  gridRows: number
  /** If set, renders a CSS gradient overlay to simulate a diagonal cut */
  diagonalOverlay?: 'piece0' | 'piece1'
}

export interface CutDef {
  type: PuzzleCutType
  gridCols: number
  gridRows: number
  pieces: PieceDef[]
}

export interface PuzzleRound {
  imageItem: GameItem
  cutDef: CutDef
  anchorPieces: PieceDef[]
  missingPiece: PieceDef
  alternatives: PuzzleAlt[]
}

export interface PuzzleAlt {
  id: string
  imageItem: GameItem
  piece: PieceDef
  isCorrect: boolean
}

export interface PairsCard {
  id: string           // `${imageId}-${pieceId}`
  imageId: number
  imageItem: GameItem
  piece: PieceDef
  cutDef: CutDef
}

export const DIFFICULTY_CONFIGS = {
  easy:   { totalPieces: 2, altCount: 3,  puzzleRounds: 5,  pairsCount: 3  },
  medium: { totalPieces: 4, altCount: 6,  puzzleRounds: 8,  pairsCount: 8  },
  hard:   { totalPieces: 6, altCount: 9,  puzzleRounds: 10, pairsCount: 12 },
} as const

export type DifficultyConfig = typeof DIFFICULTY_CONFIGS[PuzzleDifficulty]

export function getDifficultyConfig(difficulty: PuzzleDifficulty): DifficultyConfig {
  return DIFFICULTY_CONFIGS[difficulty]
}

export function getCutDef(imageId: number, difficulty: PuzzleDifficulty): CutDef {
  if (difficulty === 'easy') {
    const variant = imageId % 3
    if (variant === 0) return makeGridCut('vertical', 2, 1)
    if (variant === 1) return makeGridCut('horizontal', 1, 2)
    return makeDiagonalCut()
  }
  if (difficulty === 'medium') return makeGridCut('vertical', 2, 2)
  return makeGridCut('vertical', 3, 2) // hard: 6 pieces (3×2)
}

function makeGridCut(type: PuzzleCutType, gridCols: number, gridRows: number): CutDef {
  const pieces: PieceDef[] = []
  for (let row = 0; row < gridRows; row++) {
    for (let col = 0; col < gridCols; col++) {
      pieces.push({ id: String(pieces.length), col, row, gridCols, gridRows })
    }
  }
  return { type, gridCols, gridRows, pieces }
}

function makeDiagonalCut(): CutDef {
  // gridCols/gridRows = 1 on each piece so getPieceStyle shows the full image;
  // the diagonalOverlay gradient clips to the correct triangle in rendering.
  return {
    type: 'diagonal',
    gridCols: 1,
    gridRows: 1,
    pieces: [
      { id: '0', col: 0, row: 0, gridCols: 1, gridRows: 1, diagonalOverlay: 'piece0' },
      { id: '1', col: 0, row: 0, gridCols: 1, gridRows: 1, diagonalOverlay: 'piece1' },
    ],
  }
}

export function buildPuzzleRounds(
  images: GameItem[],
  difficulty: PuzzleDifficulty,
): PuzzleRound[] {
  const { altCount, puzzleRounds } = getDifficultyConfig(difficulty)
  if (images.length < 2) return []

  const count = Math.min(puzzleRounds, images.length)
  const selected = shuffle([...images]).slice(0, count)

  return selected.map(img => {
    const cutDef = getCutDef(img.id, difficulty)
    const missingPiece = shuffle([...cutDef.pieces])[0]
    const anchorPieces = cutDef.pieces.filter(p => p.id !== missingPiece.id)

    const actualAltCount = Math.min(altCount, images.length)
    const distractors = shuffle(images.filter(i => i.id !== img.id))
      .slice(0, actualAltCount - 1)

    const alternatives: PuzzleAlt[] = shuffle([
      { id: `correct-${img.id}`, imageItem: img, piece: missingPiece, isCorrect: true },
      ...distractors.map(di => ({
        id: `distractor-${di.id}`,
        imageItem: di,
        // same grid position as the missing piece — from a different image
        piece: { ...missingPiece, id: `d-${di.id}` },
        isCorrect: false,
      })),
    ])

    return { imageItem: img, cutDef, anchorPieces, missingPiece, alternatives }
  })
}

// Pairs mode always uses a vertical 2-piece cut for consistent card shapes
const PAIRS_CUT_DEF: CutDef = {
  type: 'vertical',
  gridCols: 2,
  gridRows: 1,
  pieces: [
    { id: '0', col: 0, row: 0, gridCols: 2, gridRows: 1 },
    { id: '1', col: 1, row: 0, gridCols: 2, gridRows: 1 },
  ],
}

export function buildPairsCards(
  images: GameItem[],
  difficulty: PuzzleDifficulty,
): PairsCard[] {
  const { pairsCount } = getDifficultyConfig(difficulty)
  if (images.length < 2) return []

  const count = Math.min(pairsCount, images.length)
  const selected = shuffle([...images]).slice(0, count)

  const cards: PairsCard[] = []
  selected.forEach(img => {
    PAIRS_CUT_DEF.pieces.forEach(piece => {
      cards.push({
        id: `${img.id}-${piece.id}`,
        imageId: img.id,
        imageItem: img,
        piece,
        cutDef: PAIRS_CUT_DEF,
      })
    })
  })

  return shuffle(cards)
}

/**
 * Returns background-image CSS properties to render a piece as a crop of the
 * full image. The container div clips to the piece's portion of the image.
 */
export function getPieceStyle(piece: PieceDef, imageUrl: string): Record<string, string> {
  const { col, row, gridCols, gridRows } = piece
  const xPercent = gridCols <= 1 ? 0 : (col / (gridCols - 1)) * 100
  const yPercent = gridRows <= 1 ? 0 : (row / (gridRows - 1)) * 100
  return {
    backgroundImage: `url(${imageUrl})`,
    backgroundSize: `${gridCols * 100}% ${gridRows * 100}%`,
    backgroundPosition: `${xPercent}% ${yPercent}%`,
    backgroundRepeat: 'no-repeat',
  }
}

/**
 * Returns the CSS gradient string for the diagonal overlay that creates
 * the visual illusion of a diagonal cut edge on a rectangular piece.
 */
export function getDiagonalOverlayGradient(overlay: 'piece0' | 'piece1', bgColor: string): string {
  // piece0: show bottom-left triangle, hide top-right
  // piece1: show top-right triangle, hide bottom-left
  if (overlay === 'piece0') {
    return `linear-gradient(to top right, transparent 50%, ${bgColor} 50%)`
  }
  return `linear-gradient(to bottom left, transparent 50%, ${bgColor} 50%)`
}
